import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Tag, X, Banknote, CreditCard } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { http, inr, inr2, errMsg, itemKey } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const field = "h-12 w-full rounded-md border border-input bg-card px-4 text-sm outline-none transition-colors focus:border-brass";
const Field = ({ label, children }) => (
  <label className="block space-y-1.5"><span className="text-sm font-medium">{label}</span>{children}</label>
);

const Coupon = ({ subtotal, coupon, setCoupon }) => {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: list = [] } = useQuery({ queryKey: ["coupons"], queryFn: () => http.get("/coupons").then((r) => r.data) });
  const apply = async (c) => {
    setBusy(true);
    try {
      const { data } = await http.post("/coupons/validate", { code: c, subtotal });
      setCoupon(data); setCode("");
      toast.success(`${data.code} applied. You save ${inr(data.discount)}.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setBusy(false); }
  };
  if (coupon) return (
    <div data-testid="applied-coupon" className="flex items-center justify-between rounded-md border border-dashed border-neem bg-neem/10 px-4 py-3 text-sm">
      <span className="flex items-center gap-2"><Tag className="h-4 w-4" /><b>{coupon.code}</b> {coupon.label}</span>
      <button data-testid="remove-coupon-button" onClick={() => setCoupon(null)} aria-label="Remove coupon"><X className="h-4 w-4" /></button>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input data-testid="coupon-input" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className={`${field} h-11`} />
        <button data-testid="coupon-apply-button" disabled={!code || busy} onClick={() => apply(code)} className="btn-outline px-5 py-2">Apply</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((c) => (
          <button key={c.code} data-testid={`coupon-suggestion-${c.code}`} onClick={() => apply(c.code)} className="chip hover:border-brass" title={c.label}>
            <span className="font-display text-sm">{c.code}</span><span className="text-muted-foreground">{c.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const Summary = ({ coupon, setCoupon, onPlace, busy }) => {
  const { cart, subtotal, mrpTotal, taxBase } = useStore();
  const discount = coupon?.discount || 0;
  const tax = subtotal ? Math.round(taxBase * ((subtotal - discount) / subtotal)) : 0;
  const total = subtotal - discount + tax;
  const halfRate = (cart.find((i) => i.gst_exclusive)?.gst_rate || 18) / 2;
  return (
    <aside className="space-y-5 rounded-md border border-border bg-card p-6 lg:sticky lg:top-32">
      <h2 className="text-2xl">Order summary</h2>
      <ul className="space-y-4">
        {cart.map((i) => (
          <li key={itemKey(i)} data-testid={`summary-item-${itemKey(i).replace(":", "-")}`} className="flex items-center gap-3 text-sm">
            <div className="relative"><img src={i.image} alt="" className="h-14 w-14 rounded object-cover" /><span className="absolute -right-2 -top-2 grid h-5 w-5 place-items-center rounded-full bg-walnut text-[10px] text-linen">{i.qty}</span></div>
            <span className="flex-1">{i.name}{i.variant_name && <span className="block text-xs text-muted-foreground">Finish: {i.variant_name}</span>}</span><span className="font-semibold">{inr(i.price * i.qty)}</span>
          </li>
        ))}
      </ul>
      <Coupon subtotal={subtotal} coupon={coupon} setCoupon={setCoupon} />
      <dl className="divide-y divide-border border-t border-border text-sm [&>div]:py-2">
        {mrpTotal > subtotal && <div className="flex justify-between"><dt className="text-muted-foreground">MRP total</dt><dd className="line-through">{inr(mrpTotal)}</dd></div>}
        <div className="flex justify-between"><dt>Subtotal</dt><dd data-testid="summary-subtotal">{inr2(subtotal)}</dd></div>
        {discount > 0 && <div className="flex justify-between text-neem dark:text-jute"><dt>Coupon {coupon.code}</dt><dd data-testid="summary-discount">-{inr2(discount)}</dd></div>}
        {tax > 0 && <div className="flex justify-between"><dt>CGST {halfRate}%</dt><dd data-testid="summary-cgst">{inr2(tax / 2)}</dd></div>}
        {tax > 0 && <div data-testid="summary-tax" className="flex justify-between"><dt>SGST {halfRate}%</dt><dd data-testid="summary-sgst">{inr2(tax / 2)}</dd></div>}
        <div className="flex justify-between"><dt>Delivery</dt><dd className="font-semibold text-neem dark:text-jute">Free</dd></div>
        <div className="flex justify-between border-t-2 border-foreground/80 text-base font-semibold"><dt>Total</dt><dd data-testid="summary-total">{inr2(total)}</dd></div>
        <p className="small-print !border-0 pt-2">Total includes all GST.{mrpTotal - subtotal + discount > 0 && ` You save ${inr(mrpTotal - subtotal + discount)}.`}</p>
      </dl>
      <button data-testid="checkout-submit-button" form="checkout-form" disabled={busy} className="btn-sheesham w-full py-4 text-base">{busy ? "Placing order" : `Place order · ${inr(total)}`}</button>
    </aside>
  );
};

const EMPTY = { full_name: "", phone: "", email: "", line1: "", line2: "", pincode: "", city: "", state: "" };

export default function Checkout() {
  const { cart, clearCart } = useStore();
  const navigate = useNavigate();
  const [a, setA] = useState(() => ({ ...EMPTY, city: "Hyderabad", state: "Telangana", pincode: localStorage.getItem("hw_pin") || "" }));
  const [coupon, setCoupon] = useState(null);
  const [eta, setEta] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setA({ ...a, [k]: e.target.value });

  useEffect(() => {
    if (a.pincode.length !== 6) return setEta(null);
    http.post("/pincode/check", { pincode: a.pincode }).then((r) => setEta(r.data)).catch(() => setEta({ serviceable: false, message: "Enter a valid 6-digit PIN code" }));
  }, [a.pincode]);
  useEffect(() => setCoupon(null), [cart]);

  const place = async (e) => {
    e.preventDefault();
    if (eta && !eta.serviceable) return toast.error(eta.message);
    setBusy(true);
    try {
      const { data } = await http.post("/orders", { items: cart.map((i) => ({ slug: i.slug, variant: i.variant || null, qty: i.qty })), address: a, coupon: coupon?.code });
      clearCart();
      navigate(`/order/${data.order_number}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  if (cart.length === 0) return (
    <div data-testid="checkout-empty" className="container-hw py-24 text-center">
      <h1 className="text-4xl">Your cart is empty.</h1>
      <Link to="/shop" className="btn-sheesham mt-8">Find something to build</Link>
    </div>
  );

  return (
    <div className="container-hw grid gap-10 py-12 lg:grid-cols-12 lg:py-16">
      <form id="checkout-form" onSubmit={place} className="space-y-10 lg:col-span-7">
        <div><p className="eyebrow">Checkout</p><h1 className="mt-3 text-4xl sm:text-5xl">Where in Hyderabad should we send the box?</h1></div>
        <section className="grid gap-5 sm:grid-cols-2">
          <Field label="Full name"><input data-testid="checkout-name-input" required value={a.full_name} onChange={set("full_name")} className={field} /></Field>
          <Field label="Mobile number">
            <div className="flex"><span className="grid place-items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm">+91</span>
              <input data-testid="checkout-phone-input" required inputMode="numeric" maxLength={10} value={a.phone} onChange={(e) => setA({ ...a, phone: e.target.value.replace(/\D/g, "") })} className={`${field} rounded-l-none`} /></div>
          </Field>
          <Field label="Email (for your order updates)"><input data-testid="checkout-email-input" type="email" required value={a.email} onChange={set("email")} className={field} /></Field>
          <Field label="PIN code">
            <input data-testid="checkout-pincode-input" required inputMode="numeric" maxLength={6} value={a.pincode} onChange={(e) => setA({ ...a, pincode: e.target.value.replace(/\D/g, "") })} className={field} />
            {eta && <span data-testid="checkout-pincode-status" className={`block text-xs ${eta.serviceable ? "text-neem dark:text-jute" : "text-destructive"}`}>{eta.serviceable ? `${eta.message}. Arrives ${eta.from_date} to ${eta.to_date}.` : eta.message}</span>}
          </Field>
          <div className="sm:col-span-2"><Field label="Flat, house no., building"><input data-testid="checkout-line1-input" required value={a.line1} onChange={set("line1")} className={field} /></Field></div>
          <div className="sm:col-span-2"><Field label="Area, street, landmark (optional)"><input data-testid="checkout-line2-input" value={a.line2} onChange={set("line2")} className={field} /></Field></div>
          <Field label="City">
            <Select value={a.city} onValueChange={(v) => setA({ ...a, city: v })}>
              <SelectTrigger data-testid="checkout-city-select" className="h-12 bg-card"><SelectValue /></SelectTrigger>
              <SelectContent>{["Hyderabad", "Secunderabad"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="State"><input data-testid="checkout-state-input" readOnly value="Telangana" className={`${field} bg-muted text-muted-foreground`} /></Field>
          <p className="text-xs text-muted-foreground sm:col-span-2">We deliver only within Hyderabad and Secunderabad for now.</p>
        </section>
        <section className="space-y-3">
          <h2 className="text-2xl">Payment</h2>
          <div data-testid="payment-pay-on-delivery" className="flex items-start gap-4 rounded-md border-2 border-sheesham bg-card p-5">
            <Banknote className="mt-0.5 h-5 w-5 text-sheesham dark:text-brass" />
            <div><p className="font-semibold">Pay on delivery</p><p className="text-sm text-muted-foreground">Cash or UPI when the box reaches your door.</p></div>
          </div>
          <div data-testid="payment-online-disabled" className="flex items-start gap-4 rounded-md border border-dashed border-border p-5 opacity-60">
            <CreditCard className="mt-0.5 h-5 w-5" />
            <div><p className="font-semibold">UPI, cards, netbanking online</p><p className="text-sm text-muted-foreground">Coming soon.</p></div>
          </div>
        </section>
      </form>
      <div className="lg:col-span-5"><Summary coupon={coupon} setCoupon={setCoupon} busy={busy} /></div>
    </div>
  );
}
